// ─────────────────────────────────────────────────────────────────────────────
// gesture_matcher.ts — $1-style template-based glyph recognizer
//
// Matches a hand-drawn stroke against a library of glyph templates.
// Pipeline per candidate:
//   1. Resample stroke → N points evenly spaced by arc length.
//   2. Translate centroid → origin; scale so max(|x|,|y|) = 1
//      (preserves aspect ratio — wind ≠ tall-wind).
//   3. For each template that passes the bbox aspect-ratio pre-cull:
//      • optionally try with optimal Procrustes rotation,
//      • optionally try with X-mirrored stroke.
//      Distance = mean squared point-to-point distance after alignment.
//   4. Return the lowest distance below template.threshold,
//      with confidence = 1 − dist / threshold.
//
// Adding a new glyph is one line in `gesture_templates.ts` — just supply a
// roughly-traced reference drawing.  Adding multiple references for the
// same glyph is also one line each — register the template multiple times
// with the same `id`; the matcher takes the best match across all of them.
// ─────────────────────────────────────────────────────────────────────────────

export interface GestureTemplate {
  /** Spell id this glyph maps to (e.g. 'wind.fly'). */
  id: string;
  /** Hand-traced reference points.  Will be resampled + normalized. */
  points: ReadonlyArray<{ x: number; y: number }>;
  /** Optional bbox aspect-ratio (w/h) range for fast pre-cull. */
  aspectRange?: [number, number];
  /** If true, the matcher tries the best Procrustes rotation.  Default: false
   *  (orientation matters — wind blows in a direction, ▲ ≠ ▼, etc.). */
  rotationInvariant?: boolean;
  /** If true, also try the X-mirrored stroke.  Default: false.  Useful for
   *  glyphs whose mirror is semantically the same (wind blowing either
   *  direction). */
  reflectionAllowed?: boolean;
  /** Mean-squared-distance threshold below which we declare a match.
   *  Default: ~0.025 (≈ 0.16 RMS in normalized coords).  Lower = stricter. */
  threshold?: number;
}

export interface GestureMatchResult {
  id: string;
  confidence: number;
  /** Distance of the winning template (for debugging / logging). */
  distance: number;
}

const RESAMPLE_N = 64;
const DEFAULT_THRESHOLD = 0.025;

type Pt = { x: number; y: number };

// ── Resampling ──────────────────────────────────────────────────────────────

function resample(points: ReadonlyArray<Pt>, n: number): Pt[] {
  if (points.length < 2) {
    const fill = points[0] ?? { x: 0, y: 0 };
    return Array.from({ length: n }, () => ({ x: fill.x, y: fill.y }));
  }

  // Total arc length
  let totalLen = 0;
  for (let i = 1; i < points.length; i++) {
    totalLen += Math.hypot(points[i].x - points[i - 1].x, points[i].y - points[i - 1].y);
  }
  if (totalLen < 1e-6) {
    return Array.from({ length: n }, () => ({ x: points[0].x, y: points[0].y }));
  }

  const step = totalLen / (n - 1);
  const out: Pt[] = [{ x: points[0].x, y: points[0].y }];
  let accum = 0;

  // Use a working copy that supports virtual subdivision (no mutating input)
  const work: Pt[] = points.map(p => ({ x: p.x, y: p.y }));
  let i = 1;
  while (out.length < n && i < work.length) {
    const dx = work[i].x - work[i - 1].x;
    const dy = work[i].y - work[i - 1].y;
    const segLen = Math.hypot(dx, dy);
    if (accum + segLen >= step && segLen > 1e-9) {
      const t = (step - accum) / segLen;
      const px = work[i - 1].x + dx * t;
      const py = work[i - 1].y + dy * t;
      out.push({ x: px, y: py });
      // Splice the new point in so the next segment continues from it
      work.splice(i, 0, { x: px, y: py });
      accum = 0;
      i++;
    } else {
      accum += segLen;
      i++;
    }
  }
  // Pad if numerical drift left us short
  const last = work[work.length - 1];
  while (out.length < n) out.push({ x: last.x, y: last.y });
  return out;
}

// ── Normalization ───────────────────────────────────────────────────────────

function normalize(points: ReadonlyArray<Pt>): Pt[] {
  let cx = 0, cy = 0;
  for (const p of points) { cx += p.x; cy += p.y; }
  cx /= points.length;
  cy /= points.length;

  let maxDim = 0;
  for (const p of points) {
    const ax = Math.abs(p.x - cx);
    const ay = Math.abs(p.y - cy);
    if (ax > maxDim) maxDim = ax;
    if (ay > maxDim) maxDim = ay;
  }
  if (maxDim < 1e-6) return points.map(() => ({ x: 0, y: 0 }));

  return points.map(p => ({ x: (p.x - cx) / maxDim, y: (p.y - cy) / maxDim }));
}

// ── Distance helpers ────────────────────────────────────────────────────────

function meanSquaredDistance(a: ReadonlyArray<Pt>, b: ReadonlyArray<Pt>): number {
  let sum = 0;
  for (let i = 0; i < a.length; i++) {
    const dx = a[i].x - b[i].x;
    const dy = a[i].y - b[i].y;
    sum += dx * dx + dy * dy;
  }
  return sum / a.length;
}

/**
 * Dynamic Time Warping distance with a Sakoe-Chiba band constraint.
 *
 * Both inputs are point sequences.  Returns the minimum-cost alignment
 * cost, where cost between two points is Euclidean distance.  The band
 * (`window`) restricts how far the warp path can stray from the
 * diagonal — without it, DTW will happily match the start of one
 * sequence to the end of the other for a low-distance candidate.
 *
 * Cost is averaged over the path length so the returned value is
 * roughly the mean per-step distance — directly comparable to
 * `meanSquaredDistance` only after sqrt, but in practice we calibrate
 * thresholds independently anyway.
 *
 * Why DTW over Procrustes mean-squared:
 *   - Procrustes is rigid (translate + rotate + uniform scale).  After
 *     alignment, point-to-point distances penalise non-uniform
 *     stretches — e.g. the user drew a bigger spiral relative to the
 *     tail, the spiral points map to "wrong" template points and the
 *     mean-squared distance blows up.
 *   - DTW lets the warp path stretch and compress along arc length,
 *     so a bigger spiral matched against a smaller spiral pays only
 *     for the *shape* mismatch, not for the size-ratio mismatch.
 *
 * Time complexity O(N · 2W).  At N=64, W=8, that's ~1K ops/distance,
 * trivial cost per match.
 */
function dtwDistance(
  a: ReadonlyArray<Pt>,
  b: ReadonlyArray<Pt>,
  window = 8,
): number {
  const N = a.length;
  const M = b.length;
  const W = Math.max(window, Math.abs(N - M));

  // Two rolling rows for memory efficiency
  let prev = new Float64Array(M + 1).fill(Infinity);
  let curr = new Float64Array(M + 1).fill(Infinity);
  prev[0] = 0;

  for (let i = 1; i <= N; i++) {
    curr.fill(Infinity);
    const jLow = Math.max(1, i - W);
    const jHigh = Math.min(M, i + W);
    for (let j = jLow; j <= jHigh; j++) {
      const dx = a[i - 1].x - b[j - 1].x;
      const dy = a[i - 1].y - b[j - 1].y;
      const stepCost = Math.sqrt(dx * dx + dy * dy);
      const best = Math.min(prev[j], curr[j - 1], prev[j - 1]);
      curr[j] = stepCost + best;
    }
    const tmp = prev;
    prev = curr;
    curr = tmp;
  }
  return prev[M] / N;
}

/** Closed-form optimal-rotation angle that minimizes ||R·a − b||² with
 *  a,b centred at origin (Kabsch in 2D). */
function procrustesAngle(a: ReadonlyArray<Pt>, b: ReadonlyArray<Pt>): number {
  let cross = 0, dot = 0;
  for (let i = 0; i < a.length; i++) {
    cross += a[i].x * b[i].y - a[i].y * b[i].x;
    dot += a[i].x * b[i].x + a[i].y * b[i].y;
  }
  return Math.atan2(cross, dot);
}

function rotate(points: ReadonlyArray<Pt>, angle: number): Pt[] {
  const c = Math.cos(angle), s = Math.sin(angle);
  return points.map(p => ({ x: p.x * c - p.y * s, y: p.x * s + p.y * c }));
}

function reflectX(points: ReadonlyArray<Pt>): Pt[] {
  return points.map(p => ({ x: -p.x, y: p.y }));
}

// ── Template caching ────────────────────────────────────────────────────────
// Templates are static — resample + normalize once and reuse.

const _cache = new WeakMap<GestureTemplate, Pt[]>();
function normalizedTemplate(tmpl: GestureTemplate): Pt[] {
  let cached = _cache.get(tmpl);
  if (!cached) {
    cached = normalize(resample(tmpl.points, RESAMPLE_N));
    _cache.set(tmpl, cached);
  }
  return cached;
}

// ── Public matcher ──────────────────────────────────────────────────────────

export function matchGesture(
  strokePoints: ReadonlyArray<Pt>,
  templates: ReadonlyArray<GestureTemplate>,
): GestureMatchResult | null {
  if (strokePoints.length < 4 || templates.length === 0) return null;

  // Stroke aspect for pre-cull
  let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
  for (const p of strokePoints) {
    if (p.x < minX) minX = p.x;
    if (p.x > maxX) maxX = p.x;
    if (p.y < minY) minY = p.y;
    if (p.y > maxY) maxY = p.y;
  }
  const w = Math.max(1, maxX - minX);
  const h = Math.max(1, maxY - minY);
  const aspect = w / h;

  const normalized = normalize(resample(strokePoints, RESAMPLE_N));
  let reflected: Pt[] | null = null;

  let best: GestureMatchResult | null = null;

  for (const tmpl of templates) {
    if (tmpl.aspectRange) {
      const [aMin, aMax] = tmpl.aspectRange;
      if (aspect < aMin || aspect > aMax) continue;
    }
    const tNormalized = normalizedTemplate(tmpl);

    let dist = computeDist(normalized, tNormalized, !!tmpl.rotationInvariant);
    if (tmpl.reflectionAllowed) {
      if (!reflected) reflected = reflectX(normalized);
      const dRef = computeDist(reflected, tNormalized, !!tmpl.rotationInvariant);
      if (dRef < dist) dist = dRef;
    }

    const threshold = tmpl.threshold ?? DEFAULT_THRESHOLD;
    if (dist <= threshold) {
      const confidence = 1 - dist / threshold;
      if (!best || confidence > best.confidence) {
        best = { id: tmpl.id, confidence, distance: dist };
      }
    }
  }

  return best;
}

function computeDist(stroke: Pt[], template: Pt[], rotationInvariant: boolean): number {
  // Rigid alignment first (translate + scale already baked into normalize;
  // here we add optional optimal rotation via Procrustes).  After alignment,
  // DTW measures shape similarity with arc-length warping freedom — handles
  // "spiral grew but tail is the same" gracefully where mean-squared can't.
  const aligned = rotationInvariant
    ? rotate(stroke, procrustesAngle(stroke, template))
    : stroke;
  return dtwDistance(aligned, template, 8);
}
